export const LOS_ANGELES_TIMEZONE = "America/Los_Angeles";

const getDateTimeParts = (value: Date) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: LOS_ANGELES_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(value);

  return Object.fromEntries(parts
    .filter((part) => part.type !== "literal")
    .map((part) => [part.type, part.value]));
};

export const formatLosAngelesDateTimeLocalInput = (value?: string | Date) => {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value.trim())) {
    return value.trim();
  }
  const date = value instanceof Date ? value : value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const parts = getDateTimeParts(date);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

export const getLosAngelesDateKey = (value?: string | Date) => {
  const localValue = formatLosAngelesDateTimeLocalInput(value);
  return localValue ? localValue.slice(0, 10) : "";
};

export const getLosAngelesTimeKey = (value?: string | Date) => {
  const localValue = formatLosAngelesDateTimeLocalInput(value);
  return localValue ? localValue.slice(11, 16) : "";
};

export const getLosAngelesCalendarValue = (dateKey?: string) => {
  const normalizedDateKey = String(dateKey || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedDateKey)) {
    return null;
  }

  const [year, month, day] = normalizedDateKey.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
};

export const getLosAngelesDateKeyFromCalendarValue = (value?: Date | Date[] | null) => {
  const resolvedValue = Array.isArray(value) ? value[0] : value;
  if (!(resolvedValue instanceof Date) || Number.isNaN(resolvedValue.getTime())) {
    return "";
  }

  return getLosAngelesDateKey(resolvedValue);
};

export const combineLosAngelesDateAndTime = (dateKey: string, timeKey: string) => {
  const normalizedDateKey = String(dateKey || "").trim();
  const normalizedTimeKey = String(timeKey || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedDateKey) || !/^\d{2}:\d{2}$/.test(normalizedTimeKey)) {
    return "";
  }

  const [year, month, day] = normalizedDateKey.split("-").map(Number);
  const [hour, minute] = normalizedTimeKey.split(":").map(Number);
  const wallClockMs = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  let utcMs = wallClockMs;

  // Resolve the UTC offset from the requested wall-clock time so this remains
  // correct during Pacific daylight and standard time.
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = getDateTimeParts(new Date(utcMs));
    const representedWallClockMs = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      0,
      0,
    );
    utcMs = wallClockMs - (representedWallClockMs - utcMs);
  }

  return new Date(utcMs).toISOString();
};

export const updateLosAngelesDateTimePart = (value: string, nextDateKey: string, nextTimeKey?: string) => {
  const currentLocalValue = formatLosAngelesDateTimeLocalInput(value);
  const dateKey = String(nextDateKey || "").trim() || currentLocalValue.slice(0, 10);
  const timeKey = String(nextTimeKey || "").trim() || currentLocalValue.slice(11, 16);
  return dateKey && timeKey ? `${dateKey}T${timeKey}` : "";
};

export const LOS_ANGELES_TIME_OPTIONS = Array.from({ length: 96 }, (_, index) => {
  const hour = Math.floor(index / 4);
  const minute = (index % 4) * 15;
  const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  const displayHour = hour % 12 || 12;
  return {
    label: `${displayHour}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`,
    value,
  };
});
